// Invoice Service - Generate and store invoice PDFs
import { jsPDF } from 'jspdf';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

export type InvoicePaymentInstrument = 'upi' | 'card' | 'bank_transfer';

export interface InvoiceData {
    invoiceId: string;
    userId: string;
    /** Optional storage owner path for admin-generated invoices. */
    storageUserId?: string;
    userName: string;
    userEmail: string;
    planName: string;
    amount: number;
    currency: string;
    billingPeriod: 'monthly' | 'annual';
    paymentDate: Date;
    periodStart: Date;
    periodEnd: Date;
    paymentId?: string;
    /** Prefer when known (e.g. from checkout). Otherwise inferred from paymentMethodRaw only. */
    paymentInstrument?: InvoicePaymentInstrument;
    /**
     * Optional raw method text from billing (never printed verbatim if it names a payment processor).
     * Used to infer Card / UPI / Bank transfer.
     */
    paymentMethodRaw?: string;
    subscribers?: number;
    taxAmount?: number;
    taxRate?: number;
}

export interface InvoiceResult {
    success: boolean;
    downloadUrl?: string;
    invoiceId?: string;
    error?: string;
}

// Currency symbols mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
    usd: '$',
    inr: '₹',
    eur: '€',
    gbp: '£',
    aud: 'A$',
    cad: 'C$',
};

const COMPANY_DISPLAY_NAME = 'Scubamail';
const COMPANY_LEGAL_NAME = 'AUDIOVASE TECHNOLOGIES PRIVATE LIMITED';
/** Three-line layout for the invoice footer (readable on PDF). */
const COMPANY_ADDRESS_LINES: [string, string, string] = [
    '#1207/343 & 1207/1/343/1,',
    '9th MAIN, 7th SECTOR, HSR LAYOUT,',
    'BANGALORE, KARNATAKA 560102',
];
const COMPANY_BILLING_EMAIL = 'billing@scubamail.io';
const COMPANY_LOGO_PATH = '/Scubamail logo for white bg.png';

const INVOICE_TERMS_TEXT =
    'This invoice is issued for the Scubamail subscription charges and service period stated above. ' +
    'Amounts reflect the plan and billing frequency in effect when payment was completed. ' +
    'Fees paid are generally non-refundable except where required by law. ' +
    'Questions or disputes regarding this invoice should be emailed to billing@scubamail.io within thirty (30) days of the invoice date.';

class InvoiceService {
    /**
     * Generate invoice PDF and upload to Firebase Storage
     * Returns the download URL for the invoice
     */
    async generateAndUploadInvoice(data: InvoiceData): Promise<InvoiceResult> {
        try {
            console.log('📄 Generating invoice PDF for:', data.invoiceId);

            // Generate the PDF
            const pdfBlob = await this.generateInvoicePDF(data);

            // Upload to Firebase Storage
            const storageUserId = data.storageUserId || data.userId;
            const downloadUrl = await this.uploadInvoicePDF(storageUserId, data.invoiceId, pdfBlob);

            console.log('✅ Invoice generated and uploaded:', downloadUrl);

            return {
                success: true,
                downloadUrl,
                invoiceId: data.invoiceId,
            };
        } catch (error) {
            console.error('❌ Error generating invoice:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to generate invoice',
            };
        }
    }

    /**
     * Generate invoice PDF using jsPDF
     */
    private async generateInvoicePDF(data: InvoiceData): Promise<Blob> {
        const doc = new jsPDF();
        const resolvedAmount = await this.resolveInvoiceAmount(data);
        const taxAmount = data.taxAmount ?? 0;
        const subtotal = Math.max(0, resolvedAmount - taxAmount);
        const amountText = this.formatMoneyForPdf(resolvedAmount, data.currency);
        const subtotalText = this.formatMoneyForPdf(subtotal, data.currency);
        const taxText = this.formatMoneyForPdf(taxAmount, data.currency);

        // Colors
        const primaryColor: [number, number, number] = [99, 102, 241]; // Indigo
        const textDark: [number, number, number] = [17, 24, 39];
        const textGray: [number, number, number] = [107, 114, 128];

        const logoDataUrl = await this.loadLogoDataUrl();

        // Header (plain white background) — logo keeps intrinsic aspect ratio (no horizontal squeeze)
        const logoX = 20;
        const logoY = 10;
        let logoW = 0;
        let logoH = 0;
        if (logoDataUrl) {
            const targetLogoWidthMm = 62;
            try {
                const natural = await this.getImageNaturalPxFromDataUrl(logoDataUrl);
                logoW = targetLogoWidthMm;
                logoH = targetLogoWidthMm * (natural.h / natural.w);
                const maxLogoHeightMm = 26;
                if (logoH > maxLogoHeightMm) {
                    const scale = maxLogoHeightMm / logoH;
                    logoH = maxLogoHeightMm;
                    logoW *= scale;
                }
            } catch {
                logoW = targetLogoWidthMm;
                logoH = 18;
            }
            doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoW, logoH);
        }

        const contentLeftX = logoDataUrl ? logoX : 20;
        const headerContentBottomY = logoDataUrl ? logoY + logoH + 4 : 28;

        // TAX INVOICE emphasis (right)
        doc.setFontSize(17);
        doc.setFont('helvetica', 'bold');
        doc.text('TAX INVOICE', 190, 22, { align: 'right' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Invoice #${this.formatInvoiceNumberForDisplay(data.invoiceId)}`, 190, 32, { align: 'right' });
        doc.text(`Date: ${this.formatDate(data.paymentDate)}`, 190, 39, { align: 'right' });

        const billSectionY = Math.max(headerContentBottomY + 14, 52);

        // Bill To section
        doc.setTextColor(...textDark);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Bill To:', 20, billSectionY);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(...textGray);

        const billToEmail = (data.userEmail || '').trim();
        const rawBillToName = (data.userName || '').trim();
        const nameIsEmail = !!rawBillToName && !!billToEmail && rawBillToName.toLowerCase() === billToEmail.toLowerCase();

        const deriveNameFromEmail = (email: string): string => {
            const local = email.split('@')[0] || '';
            const cleaned = local.replace(/[._-]+/g, ' ').trim();
            if (!cleaned) return 'Customer';
            return cleaned
                .split(/\s+/)
                .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                .join(' ');
        };

        const billToName = nameIsEmail ? deriveNameFromEmail(billToEmail) : rawBillToName || deriveNameFromEmail(billToEmail);
        doc.text(billToName, 20, billSectionY + 8);
        doc.text(billToEmail, 20, billSectionY + 15);

        // Payment Details section
        doc.setTextColor(...textDark);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Payment Details:', 120, billSectionY);

        let dividerLineY = billSectionY + 22;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(...textGray);

        const paymentX = 120;
        const paymentLabelY = billSectionY + 8;

        if (data.paymentId) {
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...textDark);
            doc.text('Payment ID:', paymentX, paymentLabelY);

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...textGray);
            doc.setFontSize(10);

            const paymentValue = String(data.paymentId);
            const paymentValueLines = doc.splitTextToSize(paymentValue, 78);

            let py = paymentLabelY + 6;
            for (let i = 0; i < paymentValueLines.length; i++) {
                doc.text(String(paymentValueLines[i]), paymentX, py + i * 5);
            }

            const statusY = py + Math.max(0, paymentValueLines.length - 1) * 5 + 6;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(...textDark);
            doc.text('Status: Paid', paymentX, statusY);

            if (paymentValueLines.length > 1) {
                dividerLineY = billSectionY + 22 + (paymentValueLines.length - 1) * 6;
            }
        } else {
            doc.setFontSize(11);
            doc.setTextColor(...textDark);
            doc.text('Status: Paid', paymentX, paymentLabelY);
        }

        // Divider line
        doc.setDrawColor(229, 231, 235);
        doc.line(20, dividerLineY, 190, dividerLineY);

        // Invoice items — thick table header bar
        const tableX = 20;
        const tableW = 170;
        const headerBarH = 22;
        const tableTop = dividerLineY + 8;
        // Gray header bar (not blue)
        doc.setFillColor(229, 231, 235);
        doc.rect(tableX, tableTop, tableW, headerBarH, 'F');
        doc.setTextColor(...textDark);
        doc.setFontSize(10.5);
        doc.setFont('helvetica', 'bold');
        const headerMidY = tableTop + headerBarH / 2 + 3;
        doc.text('Description', 25, headerMidY);
        doc.text('Period', 100, headerMidY);
        doc.text('Amount', tableX + tableW - 5, headerMidY, { align: 'right' });

        // Invoice item row
        const itemY = tableTop + headerBarH + 14;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(...textGray);

        const description = this.buildPlanDescription(data);

        // Keep plan description within its column to avoid overlapping the "Period" column.
        const descriptionColumnWidth = 70;
        const descriptionLines = doc.splitTextToSize(description, descriptionColumnWidth);
        const descriptionLineHeight = 6;
        for (let i = 0; i < descriptionLines.length; i++) {
            doc.text(String(descriptionLines[i]), 25, itemY + i * descriptionLineHeight);
        }

        doc.text(`${this.formatDate(data.periodStart)} - ${this.formatDate(data.periodEnd)}`, 100, itemY);
        doc.setTextColor(...textDark);
        doc.setFont('helvetica', 'bold');
        doc.text(amountText, tableX + tableW - 5, itemY, { align: 'right' });

        // Totals — strict two-column block with border
        const lastDescriptionY = itemY + Math.max(0, descriptionLines.length - 1) * descriptionLineHeight;
        const totalsTop = Math.max(itemY + 18, lastDescriptionY + 12);
        const totalsLeft = 118;
        const totalsRight = 190;
        const totalsW = totalsRight - totalsLeft;
        const rowH = 11;
        doc.setDrawColor(209, 213, 219);
        doc.setLineWidth(0.35);
        doc.rect(totalsLeft, totalsTop, totalsW, rowH * 3 + 16, 'S');

        let ty = totalsTop + 9;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...textGray);
        doc.text('Subtotal', totalsLeft + 6, ty);
        doc.setTextColor(...textDark);
        doc.text(subtotalText, totalsRight - 6, ty, { align: 'right' });

        ty += rowH;
        const taxLabel = data.taxRate
            ? (data.taxRate === 0.18 ? 'GST (18%)' : `Tax (${Math.round(data.taxRate * 100)}%)`)
            : 'Tax';
        doc.setTextColor(...textGray);
        doc.text(taxLabel, totalsLeft + 6, ty);
        doc.setTextColor(...textDark);
        doc.text(taxText, totalsRight - 6, ty, { align: 'right' });

        ty += rowH + 2;
        doc.setDrawColor(229, 231, 235);
        doc.line(totalsLeft + 4, ty, totalsRight - 4, ty);
        ty += 10;

        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...textDark);
        doc.text('Total', totalsLeft + 6, ty);
        doc.setTextColor(...primaryColor);
        doc.text(amountText, totalsRight - 6, ty, { align: 'right' });

        const termsLineH = 4.25;
        const termsParagraph = doc.splitTextToSize(INVOICE_TERMS_TEXT, 170);
        const nT = termsParagraph.length;

        // Footer — terms only (no payment information block)
        const minimumBottomMargin = 22;
        const pageHeight = 297;

        // Layout offsets relative to footerY
        const supportOffsetY = 4; // footerY + supportOffsetY
        const termsHeaderOffsetY = 14; // footerY + termsHeaderOffsetY
        const termsFirstLineOffsetY = termsHeaderOffsetY + 6; // header + 6 like before
        const termsEndOffsetY = termsFirstLineOffsetY + nT * termsLineH;
        const taglineOffsetY = termsEndOffsetY + 10;
        const signatureBottomOffsetY = taglineOffsetY + 38;

        const footerY = Math.min(
            ty + 16,
            pageHeight - minimumBottomMargin - signatureBottomOffsetY
        );

        const supportY = footerY + supportOffsetY;
        doc.setTextColor(...textGray);
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'normal');
        doc.text(`Support: ${COMPANY_BILLING_EMAIL}`, 20, supportY);

        const termsStartY = footerY + termsHeaderOffsetY;
        doc.setTextColor(...textDark);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('Terms & conditions', 20, termsStartY);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...textGray);
        for (let i = 0; i < termsParagraph.length; i++) {
            doc.text(String(termsParagraph[i]), 20, footerY + termsFirstLineOffsetY + i * termsLineH);
        }

        // Signature footer
        doc.setTextColor(...textGray);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const taglineY = termsStartY + 6 + termsParagraph.length * termsLineH + 10;
        doc.text('Thank you for your business!', 105, taglineY, { align: 'center' });
        doc.text(`${COMPANY_DISPLAY_NAME} - Email Marketing Made Simple`, 105, taglineY + 8, { align: 'center' });
        doc.text(`${COMPANY_BILLING_EMAIL} | www.scubamail.io`, 105, taglineY + 16, { align: 'center' });

        // Legal entity + registered address (footer)
        let legalFooterY = taglineY + 26;
        doc.setFontSize(7.5);
        doc.setTextColor(...textGray);
        const legalNameLines = doc.splitTextToSize(COMPANY_LEGAL_NAME, 170);
        for (let i = 0; i < legalNameLines.length; i++) {
            doc.text(String(legalNameLines[i]), 105, legalFooterY + i * 3.8, { align: 'center' });
        }
        legalFooterY += legalNameLines.length * 3.8 + 2;
        for (let i = 0; i < COMPANY_ADDRESS_LINES.length; i++) {
            doc.text(COMPANY_ADDRESS_LINES[i], 105, legalFooterY + i * 3.8, { align: 'center' });
        }

        // Generate blob
        return doc.output('blob');
    }

    /** jsPDF Helvetica cannot render some currency symbols (e.g. ₹) — use ASCII-safe labels. */
    private formatMoneyForPdf(amount: number, currency: string): string {
        const safe = Number.isFinite(amount) ? amount : 0;
        const formatted = safe.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        const lc = currency.toLowerCase();
        if (lc === 'usd') return `$${formatted}`;
        if (lc === 'eur') return `EUR ${formatted}`;
        if (lc === 'gbp') return `GBP ${formatted}`;
        if (lc === 'inr') return `INR ${formatted}`;
        if (lc === 'aud') return `AUD ${formatted}`;
        if (lc === 'cad') return `CAD ${formatted}`;
        return `${currency.toUpperCase()} ${formatted}`;
    }

    private async resolveInvoiceAmount(data: InvoiceData): Promise<number> {
        if (Number.isFinite(data.amount) && data.amount > 0) {
            return data.amount;
        }
        return 0;
    }

    private buildPlanDescription(data: InvoiceData): string {
        const planLc = data.planName.trim().toLowerCase();
        const periodLabel = data.billingPeriod === 'annual' ? 'Annual' : 'Monthly';
        const contacts =
            data.subscribers != null && data.subscribers > 0
                ? `up to ${data.subscribers.toLocaleString('en-US')} contacts`
                : '';

        if (planLc === 'business') {
            const emailLabel =
                data.billingPeriod === 'monthly'
                    ? 'unlimited email sends per month'
                    : 'unlimited email sends';
            const parts = [contacts, emailLabel].filter(Boolean);
            return parts.length > 0
                ? `Business Plan — ${periodLabel} subscription (${parts.join(', ')})`
                : `Business Plan — ${periodLabel} subscription (unlimited email sends)`;
        }

        if (planLc === 'pro') {
            return contacts
                ? `Pro Plan — ${periodLabel} subscription (${contacts})`
                : `Pro Plan — ${periodLabel} subscription`;
        }

        const displayPlan = data.planName.trim() || 'Subscription';
        return contacts
            ? `${displayPlan} Plan — ${periodLabel} subscription (${contacts})`
            : `${displayPlan} Plan — ${periodLabel} subscription`;
    }

    private instrumentToLabel(i: InvoicePaymentInstrument): string {
        switch (i) {
            case 'upi':
                return 'UPI';
            case 'card':
                return 'Card';
            case 'bank_transfer':
                return 'Bank transfer';
        }
    }

    /** Map free text hints to an instrument — ignores payment processor branding. */
    private inferInstrumentFromHint(hint: string): InvoicePaymentInstrument | null {
        const t = hint.toLowerCase();
        const stripped = t.replace(/\b(dodo|stripe|razorpay|paypal)\b/g, ' ');
        const s = stripped.length >= 3 ? stripped : t;

        if (/\bupi\b|bhim|.+\@.+\b|intent|collect|vpa|paytm|phonepe|gpay|google\s*pay/.test(s)) {
            return 'upi';
        }
        if (/\bnet\s*bank|netbanking|nb\b|imps|neft|rtgs|wire\s*transfer|\bach\b|bank\s+transfer/.test(s)) {
            return 'bank_transfer';
        }
        if (/\bcard\b|credit|debit|visa|mastercard|master\b|rupay|amex|discover/.test(s)) {
            return 'card';
        }
        return null;
    }

    /** Invoice shows only UPI, Card, or Bank transfer (no gateway names). */
    private resolvePaymentInstrumentDisplay(data: InvoiceData): string {
        if (data.paymentInstrument) {
            return this.instrumentToLabel(data.paymentInstrument);
        }
        const raw = (data.paymentMethodRaw || '').trim();
        if (raw) {
            const inferred = this.inferInstrumentFromHint(raw);
            if (inferred) return this.instrumentToLabel(inferred);
        }
        return 'Card';
    }

    private async loadLogoDataUrl(): Promise<string | null> {
        try {
            const response = await fetch(COMPANY_LOGO_PATH);
            if (!response.ok) return null;
            const blob = await response.blob();
            return await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
                reader.onerror = () => reject(reader.error || new Error('Failed to read logo file'));
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.warn('Invoice logo could not be loaded:', error);
            return null;
        }
    }

    private getImageNaturalPxFromDataUrl(dataUrl: string): Promise<{ w: number; h: number }> {
        return new Promise((resolve, reject) => {
            if (typeof Image === 'undefined') {
                reject(new Error('Image API unavailable'));
                return;
            }
            const img = new Image();
            img.onload = () => {
                const w = img.naturalWidth || img.width;
                const h = img.naturalHeight || img.height;
                if (!w || !h) {
                    reject(new Error('Invalid logo dimensions'));
                    return;
                }
                resolve({ w, h });
            };
            img.onerror = () => reject(new Error('Failed to decode logo'));
            img.src = dataUrl;
        });
    }

    /**
     * Upload invoice PDF to Firebase Storage
     */
    private async uploadInvoicePDF(userId: string, invoiceId: string, pdfBlob: Blob): Promise<string> {
        const fileName = `invoices/${userId}/${invoiceId}.pdf`;
        const storageRef = ref(storage, fileName);

        // Upload the file
        await uploadBytes(storageRef, pdfBlob, {
            contentType: 'application/pdf',
            customMetadata: {
                invoiceId,
                userId,
                generatedAt: new Date().toISOString(),
            },
        });

        // Get download URL
        const downloadUrl = await getDownloadURL(storageRef);
        return downloadUrl;
    }

    /**
     * Get download URL for an existing invoice
     */
    async getInvoiceDownloadUrl(userId: string, invoiceId: string): Promise<string | null> {
        try {
            const fileName = `invoices/${userId}/${invoiceId}.pdf`;
            const storageRef = ref(storage, fileName);
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.warn('Invoice not found:', invoiceId);
            return null;
        }
    }

    /**
     * Format date for display
     */
    private formatDate(date: Date): string {
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }

    /**
     * Human-facing invoice reference (Scubamail accent). Safe for PDF and UI.
     * Native ids use SCUBA_INV_<timestamp>_<uidPrefix>; legacy ids map to SCUBA_INV-<digits or suffix>.
     */
    formatInvoiceNumberForDisplay(invoiceId: string): string {
        const id = invoiceId.trim();
        if (!id) return 'SCUBA_INV-000000';
        const prefix = 'SCUBA_INV_';
        if (id.toUpperCase().startsWith(prefix)) {
            const after = id.slice(prefix.length);
            const first = after.split('_')[0];
            if (/^\d+$/.test(first)) {
                return `SCUBA_INV-${first}`;
            }
        }
        const digits = id.replace(/\D/g, '');
        if (digits.length >= 6) {
            return `SCUBA_INV-${digits.slice(-14)}`;
        }
        const tail = id.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(-12);
        return `SCUBA_INV-${tail || '000000'}`;
    }

    /**
     * Generate a unique invoice document id (Firestore / storage filename)
     */
    generateInvoiceId(userId: string): string {
        const timestamp = Date.now();
        const userPart = userId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8) || 'USER';
        return `SCUBA_INV_${timestamp}_${userPart}`;
    }
}

export const invoiceService = new InvoiceService();
export default invoiceService;
